import { KeyPrefix, TFunction } from 'i18next';
import { FC } from 'react';
import { CoatingColourSelection, CoatingThicknessSelection } from '@steelbuy/form';
import {
    Dimensions,
    ListingMaterialAge,
    ListingMaterialOrigin,
    MaterialProperties,
    Tolerance,
    getDefinitions,
    getMaterialAges,
    getOrigins,
    getPolishes,
    getTempers,
    isMaterialWithCoating,
    isMaterialWithCoatingColour,
    isMaterialWithCoatingCoverage,
    isMaterialWithCoatingThickness,
    isMaterialWithCoatingType,
    isMaterialWithDefinition,
    isMaterialWithFinish,
    isMaterialWithMillFinish,
    isMaterialWithPlateType,
    isMaterialWithPolish,
    isMaterialWithSpecification,
    isMaterialWithSurface,
    isMaterialWithTemper,
    formatCurrency,
    CurrencyCode,
    AddressModel,
    BulkUploadDraftSellerModel,
    UploadFile,
    formatNumberForDimensions,
    formatInputToNumber,
    formatNumberForInput,
    getCurrencyFromRegion,
    getRegion,
    Mutable,
    ListingSellerDraftBulkUploadModel,
    calculateMinorUnitFromMajorUnit,
    calculateMajorUnitFromMinorUnit,
    MaterialType,
    Product,
    Shape,
    getCoatingThickness,
    getCoatings,
    getProductTypes,
    isStackableProduct,
    stackableProducts,
    getBulkUploadHeadings,
    getBulkUploadHeadingsForSummary,
    isMaterialWithLength,
    formatNumber,
    getCoatingColours,
    getCoatingTypes,
    getCoatingCoverages,
    getTheoreticalWeight,
    isMaterialWithTheoreticalWeight,
    getMillFinishes,
    getTradeUnit,
    getPricingUnit,
    getCoatingThicknessUnit,
    isTwoSidedMaterial,
    isMaterialWithTolerance,
    getBulkListingInputs,
    isMaterialWithGrade,
    MaterialInputFieldsType,
    isMaterialWithGradeFamily,
    getListingInputOrderFromRegionCode,
    isMaterialWithCoatingWeight,
    isMaterialWithSurfaceFinish,
    isMaterialWithSurfaceTreatment,
    isMaterialWithCompliance,
    getDimensionUnit,
    Literal,
    getListingGroupByField,
    isTheoreticalWeight,
    Quality,
} from '@steelbuy/ts-shared';
import { toaster } from '@steelbuy/ui-primitive';
import { entries, SortComparator } from '@steelbuy/util';
import { BulkListingStep } from './BulkListing.enums';
import { RoutePath } from '../../router/Routes';

type GroupField = 'definition' | 'materialType';

export type BulkListing = MaterialProperties &
    Dimensions & {
        id?: string; // field array id for form not to be confused with listing id
        listingId: string;
        materialId?: string;
        prime?: boolean;
        testCertificate?: UploadFile;
        picture?: UploadFile;
        age?: ListingMaterialAge;
        origin?: ListingMaterialOrigin;
        tolerance?: Tolerance;
        numberOfItems?: number;
        price?: number;
        weight?: number;
        pickupAddress?: AddressModel;
        expiresAt?: string;
        autoRenew?: boolean;
        certificateToFollow?: boolean;
        collectionAddressNotes?: string;
        description?: string;
        coatingThickness?: number;
        collectionAddress?: string;
        coatingColourValue?: string;
    };

export type BulkUploadForm = {
    autoRenew: boolean;
    materialType: MaterialType;
    shape: Shape;
    product: Product;
    prime?: boolean;
    listings: BulkListing[];
    expireDate: Date;
};

export const removeNullFields = (data: BulkListing) =>
    entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            acc[key] = value as never;
        }
        return acc;
    }, {} as BulkListing);

const formatDimension = (listing: BulkListing, dimensionField: keyof BulkListing) =>
    listing[dimensionField] ? formatNumberForDimensions(listing[dimensionField] as number) : '';

export const getTheoreticalWeightForListing = (listing: BulkListing) =>
    getTheoreticalWeight(
        listing,
        {
            width: listing.width,
            length: listing.length,
            thickness: listing.thickness,
        },
        listing.numberOfItems
    );

type Heading =
    | {
          heading: string;
      }
    | {
          getHeading: (
              material: BulkUploadForm | BulkListing,
              t: TFunction<['translation', 'uiDomain', 'domainModel']>
          ) => string;
      };

type BulkListingHeaderConfig = Heading & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getOptions?: (material: BulkListing) => any;
    mapValue?: (material: BulkListing) => string | number | undefined;
    mapInput?: (input: string) => string | number;
    mapStateForInput?: (input?: number) => string;
    measurementLiteral?: string;
    getMeasurementText?: (material: BulkUploadForm, t: TFunction<['translation', 'uiDomain', 'domainModel']>) => string;
    literalPath?: `domainModel:${KeyPrefix<'domainModel'>}`;
    showHeading?: (materialProperties: BulkUploadForm) => boolean;
};

type TableHeaders = Partial<Record<keyof BulkListing | 'theoreticalWeight', BulkListingHeaderConfig>>;

export const tableHeaders: TableHeaders = {
    materialId: { heading: 'uiDomain:createListingTeaser.attributeSku' },
    definition: {
        heading: 'domainModel:material.definition.name',
        literalPath: 'domainModel:material.definition.value',
    },
    plateType: {
        heading: 'domainModel:material.plateType.name',
        literalPath: 'domainModel:material.plateType.value',
    },
    grade: { heading: 'domainModel:material.grade.name', literalPath: 'domainModel:material.grade.value' },
    gradeFamily: {
        heading: 'domainModel:material.gradeFamily.name',
        literalPath: 'domainModel:material.gradeFamily.value',
    },
    temper: { heading: 'domainModel:material.temper.name', literalPath: 'domainModel:material.temper.value' },
    specification: {
        heading: 'domainModel:material.specification.name',
        literalPath: 'domainModel:material.specification.value',
    },
    finish: { heading: 'domainModel:material.finish.name', literalPath: 'domainModel:material.finish.value' },
    millFinish: {
        heading: 'domainModel:material.millFinish.name',
        literalPath: 'domainModel:material.millFinish.value',
    },
    polish: { heading: 'domainModel:material.polish.name', literalPath: 'domainModel:material.polish.value' },
    surface: { heading: 'domainModel:material.surface.name', literalPath: 'domainModel:material.surface.value' },
    surfaceTreatment: {
        heading: 'domainModel:material.surfaceTreatment.name',
        literalPath: 'domainModel:material.surfaceTreatment.value',
    },
    compliance: {
        heading: 'domainModel:material.compliance.name',
        literalPath: 'domainModel:material.compliance.value',
    },
    surfaceFinish: {
        heading: 'domainModel:material.surfaceFinish.name',
        literalPath: 'domainModel:material.surfaceFinish.value',
    },
    coating: { heading: 'domainModel:material.coating.name', literalPath: 'domainModel:material.coating.value' },
    coatingThicknessValue: {
        heading: 'domainModel:material.coatingThickness.name',
        getMeasurementText: (listing) => getCoatingThicknessUnit(listing),
        mapValue: (listing) => {
            const { coatingThicknessValue } = listing;
            return isTwoSidedMaterial(listing)
                ? `${coatingThicknessValue}/${coatingThicknessValue}`
                : coatingThicknessValue;
        },
    },
    coatingColour: {
        heading: 'domainModel:material.coatingColour.name',
    },
    coatingType: {
        heading: 'domainModel:material.coatingType.name',
        literalPath: 'domainModel:material.coatingType.value',
    },
    coatingWeight: {
        heading: 'domainModel:material.coatingWeight.name',
        literalPath: 'domainModel:material.coatingWeight.value',
    },
    coatingCoverage: {
        heading: 'domainModel:material.coatingCoverage.name',
        literalPath: 'domainModel:material.coatingCoverage.value',
    },
    age: { heading: 'domainModel:material.age.nameShort', literalPath: 'domainModel:material.age.value' },
    origin: { heading: 'domainModel:material.origin.name', literalPath: 'domainModel:material.origin.value' },
    width: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.fields.width', {
                unit: t(`domainModel:material.dimensions.units.${getDimensionUnit()}.label`),
            }),
        mapValue: (listing: BulkListing) => formatDimension(listing, 'width'),
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    thickness: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.fields.thickness', {
                unit: t(`domainModel:material.dimensions.units.${getDimensionUnit()}.label`),
            }),
        mapValue: (listing: BulkListing) => formatDimension(listing, 'thickness'),
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    thicknessType: {
        heading: 'domainModel:material.thicknessType.name',
        literalPath: 'domainModel:material.thicknessType.value',
    },
    tolerance: { heading: 'domainModel:material.tolerance.name', literalPath: 'domainModel:material.tolerance.value' },
    weight: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.fields.weight', {
                tradeUnit: t(`domainModel:material.tradeUnit.value.${getTradeUnit(listing)}.label`),
            }),
        mapValue: (listing: BulkListing) => formatDimension(listing, 'weight'),
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    theoreticalWeight: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.fields.theoreticalWeight', {
                tradeUnit: t(`domainModel:material.tradeUnit.value.${getTradeUnit(listing)}.label`),
            }),
        mapValue: (listing: BulkListing) =>
            formatNumber(
                getTheoreticalWeight(
                    listing,
                    {
                        width: listing.width,
                        length: listing.length,
                        thickness: listing.thickness,
                    },
                    listing.numberOfItems
                )
            ),
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    length: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.fields.length', {
                unit: t(`domainModel:material.dimensions.units.${getDimensionUnit()}.label`),
            }),
        mapValue: (listing: BulkListing) => formatDimension(listing, 'length'),
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    numberOfItems: {
        getHeading: (listing, t) =>
            isStackableProduct(listing.product)
                ? t(`uiDomain:numberOfItemsLabel.label.${listing.product as typeof stackableProducts[number]}`)
                : '',
        mapValue: (listing: BulkListing) =>
            listing.numberOfItems !== undefined ? formatNumber(listing.numberOfItems) : '',
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    picture: {
        heading: 'application.createBulkListing.fields.picture',
        showHeading: (materialProperties: BulkUploadForm) =>
            materialProperties.prime === false ||
            materialProperties.listings.some(
                (listing) => listing.quality === Quality.EXCESS || listing.quality === Quality.SECONDARY
            ),
    },
    testCertificate: { heading: 'application.createBulkListing.fields.testCertificate' },
    price: {
        getHeading: (listing, t) =>
            t('application.createBulkListing.editListings.sellingPrice', {
                pricingUnit: t(`domainModel:material.pricingUnit.value.${getPricingUnit(listing)}.label` as Literal),
            }),
        mapValue: (listing: BulkListing) => {
            if (listing?.price) {
                return formatCurrency(listing.price * 100, getCurrencyFromRegion(getRegion()) as CurrencyCode);
            }
            return '';
        },
        mapInput: formatInputToNumber,
        mapStateForInput: formatNumberForInput,
    },
    description: { heading: 'application.createBulkListing.fields.description' },
    collectionAddress: {
        heading: 'application.createBulkListing.fields.collectionAddress',
        mapValue: (listing: BulkListing) => {
            if ('pickupAddress' in listing && listing.pickupAddress !== undefined) {
                return listing.pickupAddress?.id;
            }
            return '';
        },
    },
    quality: {
        heading: 'domainModel:material.quality.name',
        literalPath: 'domainModel:material.quality.value',
    },
} as const;

export type AllTableHeaders = TableHeaders & { collectionAddress?: BulkListingHeaderConfig };

export const getFilteredHeadings = (materialProperties: BulkUploadForm, isSummary?: boolean) => {
    const headingKeys = isSummary
        ? getBulkUploadHeadingsForSummary(materialProperties, materialProperties.prime)
        : getBulkUploadHeadings(materialProperties, materialProperties.prime);
    return headingKeys.reduce((acc, headingKey: string) => {
        if (headingKey in tableHeaders) {
            const headingConfig = tableHeaders[headingKey as keyof AllTableHeaders];
            if (
                headingConfig &&
                (headingConfig.showHeading === undefined || headingConfig.showHeading(materialProperties))
            ) {
                acc[headingKey as keyof AllTableHeaders] = headingConfig;
            }
        }
        return acc;
    }, {} as AllTableHeaders);
};

export const BULK_LISTING_STEPS = Object.values(BulkListingStep);

export const mapFields = (data: BulkListing, mapperName: 'mapInput' | 'mapStateForInput' = 'mapInput') =>
    entries(data).reduce(
        (acc, [key, value]) => {
            const mapper = tableHeaders[key]?.[mapperName];
            if (typeof mapper === 'function' && !!value) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                acc[key as keyof BulkListing] = mapper(value as never) as never;
            }
            return acc;
        },
        { ...data } as BulkListing
    );

export const getStepRoute = (step: number, draftId?: string) => {
    let stepPath = '';
    if (step > 1) {
        stepPath = `/step${step}`;
    }
    let draftPath = '';
    if (draftId) {
        draftPath = `/${draftId}`;
    }
    return `${RoutePath.CREATE_BULK_LISTING_WIZARD}${draftPath}${stepPath}`;
};

export enum CertificateOption {
    PREVIOUS = 'PREVIOUS',
    NEW = 'NEW',
    TO_FOLLOW = 'TO_FOLLOW',
}

export type FormData = BulkListing & {
    testCertificateOption?: CertificateOption;
    previousCertificateId?: string;
    uploadedTestCertificateId?: string;
    uploadedPictureId?: string;
    previousPictureId?: string;
    description?: string;
    pickupAddress?: string;
    collectionAddressNotes?: string;
};

export const getListingInputs = (): {
    field: keyof MaterialInputFieldsType;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Component?: FC<any>;
    getOptions: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | ((materialProperties: MaterialProperties) => any)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        | ((materialProperties: MaterialProperties, isPrime: boolean) => any);
    sort?: SortComparator;
}[] => [
    { field: 'definition', getOptions: getDefinitions },
    { field: 'plateType', getOptions: getProductTypes },
    ...getBulkListingInputs(getRegion().regionCode),
    { field: 'millFinish', getOptions: getMillFinishes },
    { field: 'polish', getOptions: getPolishes },
    { field: 'coating', getOptions: getCoatings },
    {
        field: 'coatingThicknessValue',
        getOptions: (materialProperties: MaterialProperties) => getCoatingThickness(materialProperties),
        Component: CoatingThicknessSelection,
    },
    {
        field: 'coatingColour',
        getOptions: (materialProperties: MaterialProperties) => getCoatingColours(materialProperties),
        Component: CoatingColourSelection,
    },
    { field: 'coatingType', getOptions: getCoatingTypes },
    { field: 'coatingCoverage', getOptions: getCoatingCoverages },
    { field: 'age', getOptions: getMaterialAges, sort: () => 0 },
    { field: 'origin', getOptions: getOrigins, sort: () => 0 },
];

export const onSaveDraft = (t: TFunction<'translation'>) => {
    toaster.success(t('application.createListing.draftSuccessfulNotification'));
};

export const mapDraftToFormValues = (bulkUploadDraft: BulkUploadDraftSellerModel) =>
    bulkUploadDraft.bulkListingDrafts.map((item) => {
        const formValues: BulkListing = {
            ...item.material,
            ...item.materialDimensions,
            materialId: item?.internalSellerSku,
            weight: item?.weight,
            age: item?.age,
            origin: item?.origin,
            price: item.pricing?.netPricePerUnit?.value,
            collectionAddressNotes: item.additionalInformation,
            description: item.description,
            prime: item.prime,
            testCertificate: item.testCertificate as UploadFile | undefined,
            certificateToFollow: item.certificateToFollow,
            picture: item.picture as UploadFile | undefined,
            listingId: item.id,
            numberOfItems: item.numberOfItems,
            quality: item?.quality,
        };
        if (item.pricing?.netPricePerUnit?.value) {
            formValues.price = calculateMajorUnitFromMinorUnit(
                getCurrencyFromRegion(getRegion()),
                item.pricing?.netPricePerUnit?.value
            );
        }
        if (item.pickupAddressId) {
            formValues.pickupAddress = {
                id: item.pickupAddressId,
            } as AddressModel;
        }
        return formValues;
    });

const mapListingToDraft = (
    listing: BulkListing,
    bulkReferenceId: string
): Mutable<ListingSellerDraftBulkUploadModel> => {
    const prime = listing.quality !== undefined ? listing.quality === Quality.PRIME : listing.prime;
    let { weight } = listing;
    const isWeightTheoretical = isTheoreticalWeight(listing);
    if (isWeightTheoretical) {
        const theoreticalWeight = getTheoreticalWeightForListing(listing);
        weight = theoreticalWeight && !Number.isNaN(theoreticalWeight) ? theoreticalWeight : undefined;
    }
    const listingSellerDraft: Mutable<ListingSellerDraftBulkUploadModel> = {
        id: listing.listingId,
        bulkReferenceId,
        weight,
        age: listing.age,
        internalSellerSku: listing.materialId,
        pickupAddressId: listing.pickupAddress?.id,
        additionalInformation: listing.collectionAddressNotes,
        materialDimensions: {
            width: listing.width,
            length: listing.length,
            thickness: listing.thickness,
            thicknessType: listing.thicknessType,
        },
        origin: listing.origin,
        material: {
            materialType: listing.materialType,
            shape: listing.shape,
            product: listing.product,
            tolerance: listing.tolerance,
            grade: listing.grade,
            definition: listing.definition,
            specification: listing.specification,
            finish: listing.finish,
            plateType: listing.plateType,
            coating: listing.coating,
            coatingThicknessValue: listing.coatingThicknessValue,
            surface: listing.surface,
            coatingColour: listing.coatingColour,
            coatingType: listing.coatingType,
            coatingCoverage: listing.coatingCoverage,
            temper: listing.temper,
            millFinish: listing.millFinish,
            polish: listing.polish,
            surfaceFinish: listing.surfaceFinish,
            coatingWeight: listing.coatingWeight,
            surfaceTreatment: listing.surfaceTreatment,
            compliance: listing.compliance,
            gradeFamily: listing.gradeFamily,
        },
        description: listing.description,
        prime,
        testCertificate: listing.testCertificate,
        certificateToFollow: listing.certificateToFollow,
        picture: listing.picture,
        numberOfItems: listing.numberOfItems,
        ...(listing.quality !== undefined && { quality: listing.quality }),
    };
    if (listing.price) {
        const currencyCode = getCurrencyFromRegion(getRegion());
        listingSellerDraft.pricing = {
            netPricePerUnit: calculateMinorUnitFromMajorUnit(currencyCode, listing.price),
            currencyCode,
        };
    }
    return listingSellerDraft;
};

export const mapFormValuesToDraft = (listings: BulkListing[], bulkReferenceId: string) =>
    listings.map((listing) => mapListingToDraft(listing, bulkReferenceId));

export const TEST_CERT_TO_FOLLOW = 'TEST_CERT_TO_FOLLOW';

type CompletenessLabel =
    | {
          label: string;
      }
    | { getLabel: (t: TFunction<['uiDomain', 'domainModel']>) => string };

export type CompletenessItems = {
    items: (CompletenessLabel & {
        isComplete: boolean;
        field?: keyof AllTableHeaders;
    })[];
    completedCount: number;
};

const getHeadingLabel = (heading: keyof typeof tableHeaders) =>
    tableHeaders[heading] && 'heading' in tableHeaders[heading] ? tableHeaders[heading]?.heading : '';

const getGetHeadingFunc =
    (heading: keyof typeof tableHeaders, listing: BulkListing) => (t: TFunction<['uiDomain', 'domainModel']>) =>
        tableHeaders[heading] && 'getHeading' in tableHeaders[heading]
            ? tableHeaders[heading].getHeading(listing, t)
            : '';

export const getCompletenessItems = (listing: BulkListing, isQualityCategoriesEnabled: boolean): CompletenessItems => {
    const {
        materialId,
        quality,
        testCertificate,
        prime,
        product,
        definition,
        plateType,
        grade,
        temper,
        specification,
        surface,
        finish,
        millFinish,
        polish,
        coating,
        coatingThicknessValue,
        coatingColour,
        coatingCoverage,
        coatingType,
        origin,
        thickness,
        width,
        tolerance,
        price,
        weight,
        certificateToFollow,
        pickupAddress,
        description,
        length,
        numberOfItems,
        gradeFamily,
        compliance,
        surfaceFinish,
        surfaceTreatment,
        coatingWeight,
    } = listing;

    const completenessItemMap: Partial<
        Record<
            keyof MaterialProperties,
            {
                guard: (material?: MaterialProperties | undefined) => unknown;
                value: BulkListing[keyof MaterialProperties];
                emptyGuard?: (
                    material: MaterialProperties
                ) => Readonly<Array<MaterialProperties[keyof MaterialProperties]>>;
            }
        >
    > = {
        grade: {
            guard: isMaterialWithGrade,
            value: grade,
        },
        gradeFamily: {
            guard: isMaterialWithGradeFamily,
            value: gradeFamily,
        },
        specification: {
            guard: isMaterialWithSpecification,
            value: specification,
        },
        finish: {
            guard: isMaterialWithFinish,
            value: finish,
        },
        surface: {
            guard: isMaterialWithSurface,
            value: surface,
        },
        millFinish: {
            guard: isMaterialWithMillFinish,
            value: millFinish,
        },
        polish: {
            guard: isMaterialWithPolish,
            value: polish,
        },
        coating: {
            guard: isMaterialWithCoating,
            value: coating,
        },
        temper: {
            guard: isMaterialWithTemper,
            value: temper,
            emptyGuard: getTempers,
        },
        coatingWeight: {
            guard: isMaterialWithCoatingWeight,
            value: coatingWeight,
        },
        surfaceFinish: {
            guard: isMaterialWithSurfaceFinish,
            value: surfaceFinish,
        },
        surfaceTreatment: {
            guard: isMaterialWithSurfaceTreatment,
            value: surfaceTreatment,
        },
        compliance: {
            guard: isMaterialWithCompliance,
            value: compliance,
        },
    };

    const items: CompletenessItems['items'] = [];

    const inputOrder = getListingInputOrderFromRegionCode(getRegion().regionCode);

    const isPrime = isQualityCategoriesEnabled ? quality === Quality.PRIME : prime === true;

    const isNotPrime = isQualityCategoriesEnabled
        ? quality === Quality.EXCESS || quality === Quality.SECONDARY
        : prime === false;

    items.push({
        field: 'materialId',
        label: getHeadingLabel('materialId'),
        isComplete: materialId !== undefined,
    });

    if (isQualityCategoriesEnabled) {
        items.push({
            field: 'quality',
            label: getHeadingLabel('quality'),
            isComplete: quality !== undefined,
        });
    }

    if (isMaterialWithDefinition(listing)) {
        items.push({
            field: 'definition',
            label: getHeadingLabel('definition'),
            isComplete: definition !== undefined,
        });
    }

    if (isMaterialWithPlateType(listing)) {
        items.push({
            field: 'plateType',
            label: getHeadingLabel('plateType'),
            isComplete: plateType !== undefined,
        });
    }

    inputOrder?.forEach((input) => {
        const field = completenessItemMap[input];
        if (!field?.guard(listing) || (field?.emptyGuard && field.emptyGuard(listing).length === 0)) {
            return;
        }
        items.push({
            field: input,
            label: getHeadingLabel(input),
            isComplete: field.value !== undefined,
        });
    });

    if (isMaterialWithCoatingThickness(listing)) {
        items.push({
            field: 'coatingThicknessValue',
            label: 'domainModel:material.coatingThickness.name',
            isComplete: coatingThicknessValue !== undefined,
        });
    }

    if (isMaterialWithCoatingColour(listing)) {
        items.push({
            field: 'coatingColour',
            label: 'domainModel:material.coatingColour.name',
            isComplete: coatingColour !== undefined,
        });
    }

    if (isMaterialWithCoatingType(listing)) {
        items.push({
            field: 'coatingType',
            label: 'domainModel:material.coatingType.name',
            isComplete: coatingType !== undefined,
        });
    }

    if (isMaterialWithCoatingCoverage(listing)) {
        items.push({
            field: 'coatingCoverage',
            label: 'domainModel:material.coatingCoverage.name',
            isComplete: coatingCoverage !== undefined,
        });
    }

    items.push({
        field: 'origin',
        label: getHeadingLabel('origin'),
        isComplete: origin !== undefined,
    });

    items.push({
        field: 'thickness',
        label: 'application.rfqHeadingLabels.thickness',
        isComplete: thickness !== undefined,
    });

    items.push({
        field: 'width',
        label: 'application.rfqHeadingLabels.width',
        isComplete: width !== undefined,
    });

    if (isMaterialWithLength(listing)) {
        items.push({
            field: 'length',
            label: 'application.rfqHeadingLabels.length',
            isComplete: length !== undefined,
        });
    }

    if (isMaterialWithTolerance(listing)) {
        items.push({
            field: 'tolerance',
            label: getHeadingLabel('tolerance'),
            isComplete: tolerance !== undefined,
        });
    }

    if (isMaterialWithTheoreticalWeight(listing)) {
        let isComplete = true;
        if (
            !listing.width ||
            !listing.thickness ||
            !listing.numberOfItems ||
            getTheoreticalWeightForListing(listing) === undefined ||
            (isStackableProduct(listing.product) && !listing.length)
        ) {
            isComplete = false;
        }

        items.push({
            field: 'theoreticalWeight',
            label: 'application.checkout.theoreticalWeight',
            isComplete,
        });
    } else {
        items.push({
            field: 'weight',
            label: 'application.checkout.weight',
            isComplete: weight !== undefined,
        });
    }

    if (isPrime) {
        items.push({
            field: 'testCertificate',
            label: 'uiDomain:listingDraft.attributeCertificate',
            isComplete: testCertificate !== undefined || certificateToFollow === true,
        });
    }
    if (isNotPrime) {
        items.push({
            field: 'description',
            label: 'uiDomain:materialQualitySelection.descriptionLabel',
            isComplete: !!description,
        });
    }

    items.push({
        field: 'price',
        getLabel: getGetHeadingFunc('price', listing),
        isComplete: price !== undefined,
    });

    if (isStackableProduct(listing.product)) {
        items.push({
            field: 'numberOfItems',
            label: `uiDomain:numberOfItemsLabel.label.${product}`,
            isComplete: numberOfItems !== undefined,
        });
    }

    items.push({
        field: 'pickupAddress',
        label: 'application.createBulkListing.editListings.collectionAddress',
        isComplete: pickupAddress !== undefined,
    });

    const completedCount = items.filter((item) => item.isComplete).length;

    const itemsWithCompleteness = {
        items,
        completedCount,
    };
    return itemsWithCompleteness;
};

export const getIncompleteListingsCount = (listings: BulkListing[], isQualityCategoriesEnabled: boolean) => {
    const completenessItems = listings.map((listing) => getCompletenessItems(listing, isQualityCategoriesEnabled));
    return completenessItems.filter((listing) => listing.completedCount !== listing.items.length).length;
};

export type GroupedListings = {
    groupedBy: string;
    groupValue: string;
    listings: BulkListing[];
};

export const getListingsGrouped = (formData: BulkUploadForm, bulkListings: BulkListing[]): GroupedListings[] => {
    const groupKey = getListingGroupByField(formData);

    if (formData.materialType === MaterialType.ALUMINIUM || formData.materialType === MaterialType.STAINLESS_STEEL) {
        return [
            {
                groupedBy: groupKey,
                groupValue: formData.materialType,
                listings: bulkListings,
            },
        ];
    }
    const groupedMap = bulkListings.reduce<Record<string, BulkListing[]>>((acc, item) => {
        const value = item[groupKey as GroupField];

        if (!value) {
            return acc;
        }

        (acc[value] ??= []).push(item);
        return acc;
    }, {});

    return Object.entries(groupedMap)
        .map(([groupValue, listings]) => ({
            groupedBy: groupKey,
            groupValue,
            listings,
        }))
        .sort((a, b) => a.groupValue.localeCompare(b.groupValue));
};
